import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Megaphone, AlertTriangle, Plus, Pencil, Trash2, Loader2, X } from "lucide-react";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
import { NoticeText } from "@/components/NoticeText";
const CAN_POST = ["admin", "host", "cleaner", "subcontractor"];
const empty = { text: "", priority: "normal", expires_at: "" };

const fmtDate = (iso) => formatDate(iso);

export function NoticeBoard({ houseId }) {
  const { user } = useAuth();
  const canPost = CAN_POST.includes(user.role);
  const [notices, setNotices] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/houses/${houseId}/notices`).then(({ data }) => setNotices(data)).catch(() => setNotices([]));
  useEffect(() => { load(); }, [houseId]);

  const openNew = () => { setForm(empty); setEditing(null); setShowForm(true); };
  const openEdit = (n) => { setForm({ text: n.text_original || n.text, priority: n.priority || "normal", expires_at: n.expires_at ? n.expires_at.slice(0, 10) : "" }); setEditing(n); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { text: form.text, priority: form.priority, expires_at: form.expires_at || null };
      if (editing) await api.put(`/notices/${editing.id}`, payload);
      else await api.post(`/houses/${houseId}/notices`, payload);
      toast.success(editing ? tx("Mededeling bijgewerkt") : tx("Mededeling geplaatst"));
      setShowForm(false); setForm(empty); setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(tx("Deze mededeling verwijderen?"))) return;
    try { await api.delete(`/notices/${id}`); load(); } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-sm" data-testid="noticeboard-card">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-primary" />
          <h2 className="font-display font-semibold text-lg">{tx("Live prikbord")}</h2>
        </div>
        {canPost && !showForm && (
          <Button size="sm" onClick={openNew} className="bg-primary hover:bg-primary/90 gap-1.5" data-testid="add-notice-btn">
            <Plus className="h-4 w-4" /> {tx("Mededeling")}
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-4 rounded-lg border border-border p-4 bg-accent/30 space-y-3" data-testid="notice-form">
          <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder={tx("Bijv. reparatie kraan gepland, extra instructie voor schoonmaak…")} data-testid="notice-text-input" />
          <div className="flex flex-wrap items-end gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium" data-testid="notice-urgent-toggle">
              <input type="checkbox" checked={form.priority === "urgent"} onChange={(e) => setForm({ ...form, priority: e.target.checked ? "urgent" : "normal" })} className="h-4 w-4 accent-red-600" data-testid="notice-urgent-checkbox" />
              <AlertTriangle className="h-4 w-4 text-red-600" /> {tx("Urgent")}
            </label>
            <div>
              <Label className="text-xs">{tx("Verloopt op (optioneel)")}</Label>
              <DatePicker value={form.expires_at} onChange={(v) => setForm({ ...form, expires_at: v })} placeholder={tx("Geen einddatum")} className="mt-1 w-52" testId="notice-expiry-input" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={saving || !form.text.trim()} className="bg-primary hover:bg-primary/90 gap-2" data-testid="save-notice-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? tx("Opslaan") : tx("Plaatsen")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); }}><X className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {notices === null ? (
        <div className="flex py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : notices.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tx("Geen actieve mededelingen.")}</p>
      ) : (
        <div className="space-y-3">
          {notices.map((n) => {
            const urgent = n.priority === "urgent";
            return (
              <div key={n.id} className={`rounded-lg border p-3.5 ${urgent ? "border-red-300 bg-red-50" : "border-border bg-background"}`} data-testid={`notice-${n.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0">
                    {urgent && <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />}
                    <div className="min-w-0"><NoticeText n={n} className={`text-sm whitespace-pre-line ${urgent ? "text-red-800 font-medium" : "text-foreground"}`} testId={`notice-text-${n.id}`} /></div>
                  </div>
                  {canPost && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(n)} className="h-7 w-7" data-testid={`edit-notice-${n.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => del(n.id)} className="h-7 w-7 text-destructive" data-testid={`delete-notice-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                  <span>{n.created_by_name}</span>
                  {n.expires_at && <span>{tx("· verloopt")} {fmtDate(n.expires_at)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
